import React, { useState } from "react";
import {
  ActionIcon,
  ColorPicker,
  ColorSwatch,
  Popover,
  SimpleGrid,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconPointer,
  IconPencil,
  IconHighlight,
  IconEraser,
  IconRectangle,
  IconCircle,
  IconArrowUpRight,
  IconLetterT,
  IconMoodSmile,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconTrash,
  IconDownload,
  IconPlus,
  IconPalette,
  IconRotateClockwise,
} from "@tabler/icons-react";
import { DrawingTool, ToolSettings } from "./types/chart";
import styles from "./ChartToolbar.module.css";

const TOOLS: { tool: DrawingTool; icon: React.ReactNode; label: string }[] = [
  { tool: "select", icon: <IconPointer size={18} />, label: "Select" },
  { tool: "pen", icon: <IconPencil size={18} />, label: "Pen" },
  {
    tool: "highlighter",
    icon: <IconHighlight size={18} />,
    label: "Highlighter",
  },
  { tool: "eraser", icon: <IconEraser size={18} />, label: "Eraser" },
  { tool: "rectangle", icon: <IconRectangle size={18} />, label: "Rectangle" },
  { tool: "circle", icon: <IconCircle size={18} />, label: "Circle" },
  { tool: "arrow", icon: <IconArrowUpRight size={18} />, label: "Arrow" },
  { tool: "text", icon: <IconLetterT size={18} />, label: "Text" },
  { tool: "emoji", icon: <IconMoodSmile size={18} />, label: "Emoji" },
];

const EMOJI_PALETTE = [
  "\u{1F600}",
  "\u{1F60D}",
  "\u{1F622}",
  "\u{1F62E}",
  "\u{1F914}",
  "\u{1F637}",
  "\u{1F489}",
  "\u{1F48A}",
  "\u{1FA79}",
  "\u{1FA7A}",
  "\u{1F52C}",
  "⚕️",
  "⬆️",
  "⬇️",
  "⬅️",
  "➡️",
  "⭐",
  "❗",
  "✅",
  "❌",
  "⚠️",
  "\u{1F534}",
  "\u{1F7E1}",
  "\u{1F7E2}",
];

const COLORS = [
  "#000000",
  "#E03131",
  "#1971C2",
  "#2F9E44",
  "#E8590C",
  "#FFFFFF",
];

const WIDTHS = [
  { value: 2, size: 6 },
  { value: 4, size: 10 },
  { value: 8, size: 16 },
];

interface ChartToolbarProps {
  toolSettings: ToolSettings;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onWidthChange: (width: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Discard stroke edits since open / last save (restore saved annotations). */
  onRevertSession: () => void;
  canRevertSession: boolean;
  /** Remove every annotation permanently (empty canvas). */
  onDeleteAllAnnotations: () => void;
  canDeleteAllAnnotations: boolean;
  onDownload: () => void;
  activeEmoji?: string;
  onEmojiSelect?: (emoji: string) => void;
  /** Merged with the root toolbar layout (e.g. floating wrapper in ChartEditor) */
  className?: string;
}

export default function ChartToolbar({
  toolSettings,
  onToolChange,
  onColorChange,
  onWidthChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onRevertSession,
  canRevertSession,
  onDeleteAllAnnotations,
  canDeleteAllAnnotations,
  onDownload,
  activeEmoji,
  onEmojiSelect,
  className,
}: ChartToolbarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [mobileStyleOpen, setMobileStyleOpen] = useState(false);
  const isCustomColor = !COLORS.includes(toolSettings.color);
  const hideStyleControls =
    toolSettings.tool === "emoji" || toolSettings.tool === "eraser";

  return (
    <div className={[styles.toolbar, className].filter(Boolean).join(" ")}>
      {/* Tool selection */}
      <div className={styles.section}>
        {TOOLS.map(({ tool, icon, label }) =>
          tool === "emoji" ? (
            <Popover
              key={tool}
              opened={emojiPickerOpen}
              onChange={setEmojiPickerOpen}
              position="bottom"
              withArrow
              shadow="md"
            >
              <Popover.Target>
                <Tooltip label={label} position="bottom" withArrow>
                  <ActionIcon
                    variant={toolSettings.tool === tool ? "filled" : "subtle"}
                    color={toolSettings.tool === tool ? "teal" : "gray"}
                    size="lg"
                    onClick={() => {
                      if (toolSettings.tool !== "emoji") {
                        onToolChange(tool);
                      }
                      setEmojiPickerOpen((o) => !o);
                    }}
                    aria-label={label}
                  >
                    {icon}
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <SimpleGrid cols={6} spacing={4}>
                  {EMOJI_PALETTE.map((emoji) => (
                    <UnstyledButton
                      key={emoji}
                      onClick={() => {
                        onEmojiSelect?.(emoji);
                        onToolChange("emoji");
                        setEmojiPickerOpen(false);
                      }}
                      style={{
                        fontSize: 24,
                        lineHeight: 1,
                        padding: 4,
                        textAlign: "center",
                        borderRadius: 4,
                        background:
                          activeEmoji === emoji
                            ? "var(--mantine-color-teal-1)"
                            : undefined,
                      }}
                    >
                      {emoji}
                    </UnstyledButton>
                  ))}
                </SimpleGrid>
              </Popover.Dropdown>
            </Popover>
          ) : (
            <Tooltip key={tool} label={label} position="bottom" withArrow>
              <ActionIcon
                variant={toolSettings.tool === tool ? "filled" : "subtle"}
                color={toolSettings.tool === tool ? "teal" : "gray"}
                size="lg"
                onClick={() => onToolChange(tool)}
                aria-label={label}
              >
                {icon}
              </ActionIcon>
            </Tooltip>
          ),
        )}
      </div>

      {/* Mobile toggle for color/width */}
      {!hideStyleControls && (
        <Popover
          opened={mobileStyleOpen}
          onChange={setMobileStyleOpen}
          position="bottom"
          withArrow
          shadow="md"
        >
          <Popover.Target>
            <ActionIcon
              className={styles.mobileToggle}
              variant="subtle"
              color="gray"
              size="lg"
              onClick={() => setMobileStyleOpen((o) => !o)}
              aria-label="Style options"
            >
              <IconPalette size={18} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className={styles.section}>
                {COLORS.map((color) => (
                  <ColorSwatch
                    key={`m-${color}`}
                    color={color}
                    size={22}
                    className={`${styles.colorSwatch} ${
                      toolSettings.color === color
                        ? styles.colorSwatchSelected
                        : ""
                    }`}
                    onClick={() => onColorChange(color)}
                    style={
                      color === "#FFFFFF"
                        ? { border: "2px solid var(--mantine-color-gray-4)" }
                        : undefined
                    }
                  />
                ))}
              </div>
              <div className={styles.section}>
                {WIDTHS.map(({ value, size }) => (
                  <ActionIcon
                    key={`m-w-${value}`}
                    variant={toolSettings.width === value ? "filled" : "subtle"}
                    color={toolSettings.width === value ? "teal" : "gray"}
                    size="lg"
                    onClick={() => onWidthChange(value)}
                    aria-label={`Width ${value}`}
                  >
                    <div
                      className={styles.widthDot}
                      style={{
                        width: size,
                        height: size,
                        background:
                          toolSettings.width === value ? "white" : undefined,
                      }}
                    />
                  </ActionIcon>
                ))}
              </div>
            </div>
          </Popover.Dropdown>
        </Popover>
      )}

      {!hideStyleControls && (
        <>
          <div className={`${styles.divider} ${styles.collapsible}`} />

          {/* Color palette */}
          <div className={`${styles.section} ${styles.collapsible}`}>
            {COLORS.map((color) => (
              <ColorSwatch
                key={color}
                color={color}
                size={22}
                className={`${styles.colorSwatch} ${
                  toolSettings.color === color ? styles.colorSwatchSelected : ""
                }`}
                onClick={() => onColorChange(color)}
                style={
                  color === "#FFFFFF"
                    ? { border: "2px solid var(--mantine-color-gray-4)" }
                    : undefined
                }
              />
            ))}
            <Popover
              opened={pickerOpen}
              onChange={setPickerOpen}
              position="bottom"
              withArrow
              shadow="md"
            >
              <Popover.Target>
                <Tooltip label="Custom color" position="bottom" withArrow>
                  {isCustomColor ? (
                    <ColorSwatch
                      color={toolSettings.color}
                      size={22}
                      className={`${styles.colorSwatch} ${styles.colorSwatchSelected}`}
                      onClick={() => setPickerOpen((o) => !o)}
                    />
                  ) : (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setPickerOpen((o) => !o)}
                      aria-label="Custom color"
                    >
                      <IconPlus size={14} />
                    </ActionIcon>
                  )}
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <ColorPicker
                  value={toolSettings.color}
                  onChange={(color) => {
                    onColorChange(color);
                  }}
                  swatches={COLORS.filter((c) => c !== "#FFFFFF")}
                  size="sm"
                />
              </Popover.Dropdown>
            </Popover>
          </div>

          <div className={`${styles.divider} ${styles.collapsible}`} />

          {/* Width selector */}
          <div className={`${styles.section} ${styles.collapsible}`}>
            {WIDTHS.map(({ value, size }) => (
              <Tooltip
                key={value}
                label={`Width ${value}`}
                position="bottom"
                withArrow
              >
                <ActionIcon
                  variant={toolSettings.width === value ? "filled" : "subtle"}
                  color={toolSettings.width === value ? "teal" : "gray"}
                  size="lg"
                  onClick={() => onWidthChange(value)}
                  aria-label={`Width ${value}`}
                >
                  <div
                    className={styles.widthDot}
                    style={{
                      width: size,
                      height: size,
                      background:
                        toolSettings.width === value ? "white" : undefined,
                    }}
                  />
                </ActionIcon>
              </Tooltip>
            ))}
          </div>
        </>
      )}

      <div className={styles.divider} />

      {/* Undo/Redo/Clear/Download */}
      <div className={styles.section}>
        <Tooltip label="Undo" position="bottom" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            disabled={!canUndo}
            onClick={onUndo}
            aria-label="Undo"
          >
            <IconArrowBackUp size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Redo" position="bottom" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            disabled={!canRedo}
            onClick={onRedo}
            aria-label="Redo"
          >
            <IconArrowForwardUp size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Reset version's changes" position="bottom" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            disabled={!canRevertSession}
            onClick={onRevertSession}
            aria-label="Reset version's changes"
          >
            <IconRotateClockwise size={18} />
          </ActionIcon>
        </Tooltip>
        <div className={styles.toolbarGapDivider} aria-hidden title="" />
        <Tooltip label="Delete all annotations" position="bottom" withArrow>
          <ActionIcon
            variant="subtle"
            color="red"
            size="lg"
            disabled={!canDeleteAllAnnotations}
            onClick={onDeleteAllAnnotations}
            aria-label="Delete all annotations"
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Download PNG" position="bottom" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={onDownload}
            aria-label="Download PNG"
          >
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>
      </div>
    </div>
  );
}
